import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import { withImpersonationHeader } from '../utils/impersonation';
// Moved UsageDataService functionality directly into component
import BudgetEditModal from './BudgetEditModal';
import PricingSlider from './PricingSlider';
import { generateUsageReportPDF } from '../utils/pdfExport';
import { DashboardSkeleton, SkeletonStyles } from './SkeletonLoaders';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Utilities
const fmtUSD = (v: number) => `$${Number(v ?? 0).toFixed(2)}`;
const fmtNum = (v: number) => Number(v ?? 0).toLocaleString();

// API service for usage data
const apiCall = async (endpoint: string, token?: string, signal?: AbortSignal, options: RequestInit = {}) => { // Add options parameter
  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  headers = withImpersonationHeader(headers);

  if (options.headers) {
    headers = {
      ...headers,
      ...(options.headers as Record<string, string>),
    };
  }

  const response = await fetch(config.getApiUrl(endpoint), {
    method: 'GET',
    ...options, // Spread options to override method, body, etc.
    headers,
    signal, // Pass the signal to fetch
  });
  
  if (!response.ok) {
    throw new Error(`API call failed: ${response.status}`);
  }
  
  return response.json();
};


// Types for API responses
interface UsageSummary {
  total_calls: number;
  total_cost: number;
  total_errors: number;
  active_endpoints: number;
  total_endpoints?: number;
  period_days: number;
  calls_growth_percent?: number;
  cost_growth_percent?: number;
  monthly_endpoints_used?: number;
  monthly_usage_calls?: number;
  credits_used?: number;
  credits_limit?: number;
  credits_remaining?: number;
}

interface EndpointData {
  name: string;
  display_name?: string;
  calls: number;
  cost: number;
  errors: number;
  latency: number;
  status: 'active' | 'inactive';
  success_rate?: number;
  health_status?: 'healthy' | 'degraded' | 'down';
  route_status?: string; // ACTIVE or DEACTIVATED
}

interface ChartDataPoint {
  date: string;
  full_date: string;
  calls: number;
  cost: number;
}

interface CostBreakdownItem {
  name: string;
  value: number;
  calls?: number;
  avg_cost?: number;
}

// Types for dashboard data
interface UsageSummary {
  total_calls: number;
  total_cost: number;
  total_errors: number;
  active_endpoints: number;
  total_endpoints?: number;
  period_days: number;
  calls_growth_percent?: number;
  cost_growth_percent?: number;
  monthly_endpoints_used?: number;
  monthly_usage_calls?: number;
  credits_used?: number;
  credits_limit?: number;
  credits_remaining?: number;
}

interface DashboardData {
  summary: UsageSummary;
  chart_data: ChartDataPoint[];
  endpoints: EndpointData[];
  cost_breakdown: any[];
  monthly_totals: any[];
}

interface FilteredDashboardData extends DashboardData {
  filtered_period: number;
}

// Generate dummy data with consistent values
const generateDummyData = (days: number) => {
  const data = [];
  const today = new Date();
  const baseValues = [2500, 3200, 2800, 3500, 4100, 3900, 4500, 3700, 4200, 3300, 2900, 3600, 4000, 3800, 2700];
  const costMultiplier = 0.01; // $0.01 per call
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const calls = baseValues[i % baseValues.length];
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      calls: calls,
      cost: calls * costMultiplier,
    });
  }
  return data;
};

// Dummy endpoints data
const dummyEndpoints = [
  {
    id: '1',
    name: 'Login & Session Refresh',
    calls: 15420,
    cost: 154.20,
    latency: 245,
    status: 'active',
    data: generateDummyData(30),
  },
  {
    id: '2', 
    name: 'Orders → Fetch Recent',
    calls: 8932,
    cost: 89.32,
    latency: 187,
    status: 'active',
    data: generateDummyData(30),
  },
  {
    id: '3',
    name: 'Inventory → Update SKU',
    calls: 5218,
    cost: 78.27,
    latency: 423,
    status: 'inactive',
    data: generateDummyData(30),
  },
  {
    id: '4',
    name: 'Analytics → Dashboard Stats',
    calls: 3891,
    cost: 38.91,
    latency: 156,
    status: 'active',
    data: generateDummyData(30),
  },
];

// Removed unused pieData constant


interface DateRange {
  label: string;
  days: number;
}

interface CostBreakdownItem {
  name: string;
  value: number;
  displayValue: number;
  color: string;
  calls?: number;
  avg_cost?: number;
}

const dateRanges: DateRange[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const UsageDashboard: React.FC = React.memo(() => {
  console.log('🚀 UsageDashboard component starting to mount...');
  
  console.log('🚀 Getting auth context...');
  const { user, session } = useAuth();
  console.log('🚀 Auth context retrieved:', { user: !!user, session: !!session });
  
  console.log('🚀 Initializing state...');
  const [viewMode, setViewMode] = useState<'usage' | 'cost'>('usage');
  const [selectedRange, setSelectedRange] = useState<DateRange>(dateRanges[1]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [subView, setSubView] = useState<'endpoints' | 'breakdown'>('endpoints');
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);
  const [selectedEndpointData, setSelectedEndpointData] = useState<any>(null);
  const [endpointDataLoading, setEndpointDataLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [apiCallsFilter, setApiCallsFilter] = useState('');
  const [dollarsFilter, setDollarsFilter] = useState('');
  
  // API state
  const [dashboardData, setDashboardData] = useState<FilteredDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Budget state
  const [budget, setBudget] = useState(120); // Default budget
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  
  // Plan selection state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planUpdating, setPlanUpdating] = useState(false);
  
  // Add-on management state
  const [showAddonsModal, setShowAddonsModal] = useState(false);
  const [addonSettings, setAddonSettings] = useState({
    manual_auth: false,
    manual_maintenance: false,
    manual_auth_platforms: 0,
    manual_maintenance_platforms: 0
  });
  const [addonUpdating, setAddonUpdating] = useState(false);
  
  // Subscription state
  interface SubscriptionData {
    plan_id: string;
    credits_limit: number;
    credits_remaining: number;
    credits_used: number;
    usage_percentage?: number;
    days_until_reset?: number;
    plan_start_date?: string;
    subscription_status?: string;
    addon_manual_auth?: boolean;
    addon_manual_maintenance?: boolean;
    addon_manual_auth_platforms?: number;
    addon_manual_maintenance_platforms?: number;
  }

  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [billingData, setBillingData] = useState<any>(null);

  // Debug subscription data changes
  useEffect(() => {
    console.log('🔍 subscriptionData updated:', subscriptionData);
  }, [subscriptionData]);
  
  // Add ref to track if component is mounted
  const fetchingRef = useRef(false);
  
  // Refs for chart elements (for PDF export)
  const mainChartRef = useRef<HTMLDivElement>(null);
  const pieChartRef = useRef<HTMLDivElement>(null);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Fetch dashboard data when component mounts or selectedRange changes
  useEffect(() => {
    if (!session?.access_token) {
      console.log('🔄 No access token, skipping API call');
      setLoading(false);
      return;
    }

    console.log(`EFFECT: Starting for ${selectedRange.days} days.`);
    
    // Use a flag to prevent setting state after component unmounts
    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async (days: number) => {
      if (!isMounted) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log(`Getting dashboard data for ${days} days directly...`);
        const response = await apiCall(`/subscription/dashboard?days=${days}`, session.access_token, controller.signal);
        
        // Only update state if component is still mounted
        if (!isMounted) {
          console.log('FETCH: Component unmounted, skipping state update');
          return;
        }
        
        console.log('🔍 Full API response:', response);
        console.log('🔍 Response.data:', response.data);
        console.log('🔍 Response.budget:', response.budget);
        
        const filteredData = {
          ...response.data,
          filtered_period: days
        };
        console.log('🔍 Filtered data to set:', filteredData);
        setDashboardData(filteredData);

        // Extract subscription and billing data from the API response
        if (response.data?.subscription) {
          console.log('🔍 Subscription data received:', response.data.subscription);
          setSubscriptionData(response.data.subscription);
          // Update addon settings from subscription data
          setAddonSettings({
            manual_auth: response.data.subscription.addon_manual_auth || false,
            manual_maintenance: response.data.subscription.addon_manual_maintenance || false,
            manual_auth_platforms: response.data.subscription.addon_manual_auth_platforms || 0,
            manual_maintenance_platforms: response.data.subscription.addon_manual_maintenance_platforms || 0
          });
          console.log('🔍 Subscription data extracted:', response.data.subscription);
        }
        
        if (response.data?.billing) {
          setBillingData(response.data.billing);
          console.log('🔍 Billing data extracted:', response.data.billing);
        }

        if (response.budget?.currentMonthlyBudget !== undefined) {
          setBudget(response.budget.currentMonthlyBudget);
          console.log('🔍 Budget set to:', response.budget.currentMonthlyBudget);
        } else {
          console.log('🔍 No budget data found in response');
        }
      } catch (err: any) {
        if (!isMounted) return;
        
        if (err.name === 'AbortError') {
          console.log('FETCH: Aborted gracefully.');
          return;
        } else {
          console.error('FETCH: Failed to fetch dashboard data:', err);
          setError(err.message || 'Failed to load usage data');
        }
      } finally {
        if (isMounted) {
          console.log("FETCH: Finally block reached.");
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    // Add a small delay to prevent rapid re-mounting from causing issues
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        fetchData(selectedRange.days);
      }
    }, 100);

    // Cleanup function
    return () => {
      console.log('EFFECT: Cleanup - marking unmounted and aborting fetch.');
      isMounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [selectedRange.days, session?.access_token]);

  // Force refresh with cache clearing
  const handleForceRefresh = useCallback(async () => {
    if (!session?.access_token) return;
    
    setRefreshing(true);
    setError(null);
    
    try {
      console.log(`Force refreshing dashboard data (cache cleared) for ${selectedRange.days} days...`);
      
      // Add timestamp to force cache bust on server side too
      const timestamp = Date.now();
      const response = await apiCall(
        `/subscription/dashboard?days=${selectedRange.days}&_t=${timestamp}`, 
        session.access_token,
        undefined, // signal
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            'X-Force-Refresh': 'true'
          }
        }
      );
      
      console.log('🔍 Force refresh API response:', response);
      
      const filteredData = {
        ...response.data,
        filtered_period: selectedRange.days
      };
      setDashboardData(filteredData);

      // Extract subscription and billing data from refresh response
      if (response.data?.subscription) {
        setSubscriptionData(response.data.subscription);
        // Update addon settings from subscription data
        const addons = response.data.subscription.addon_services || {};
        setAddonSettings({
          manual_auth: addons.manual_auth || false,
          manual_maintenance: addons.manual_maintenance || false,
          manual_auth_platforms: addons.manual_auth_platforms || 0,
          manual_maintenance_platforms: addons.manual_maintenance_platforms || 0,
        });
      }

      // Handle budget data
      if (response.budget) {
        setBudget(response.budget.currentMonthlyBudget);
      }
    } catch (err: any) {
      console.error('FORCE REFRESH: Failed to force refresh dashboard data:', err);
      setError(err.message || 'Failed to refresh usage data');
    } finally {
      setRefreshing(false);
    }
  }, [session?.access_token, selectedRange.days]);

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setSelectedRange(range);
    // The useEffect will handle the fetching and loading state
  }, []);

  // Fetch endpoint-specific data
  const fetchEndpointData = useCallback(async (endpointId: string) => {
    if (!session?.access_token) return;
    
    setEndpointDataLoading(true);
    try {
      console.log(`🔍 Fetching data for endpoint: ${endpointId}`);
      const response = await apiCall(`/usage/endpoints/${encodeURIComponent(endpointId)}?days=${selectedRange.days}`, session.access_token);
      
      if (response.status === 'success') {
        setSelectedEndpointData(response);
        console.log('✅ Endpoint data loaded:', response);
      } else {
        console.error('❌ Failed to fetch endpoint data:', response);
      }
    } catch (error: any) {
      console.error('❌ Error fetching endpoint data:', error);
    } finally {
      setEndpointDataLoading(false);
    }
  }, [session?.access_token, selectedRange.days]);

  // Handle endpoint selection
  const handleEndpointClick = useCallback((endpointName: string) => {
    setSelectedEndpoint(endpointName);
    fetchEndpointData(endpointName);
  }, [fetchEndpointData]);


  const handleRefresh = useCallback(async () => {
    if (!session?.access_token) return;
    
    setRefreshing(true);
    setError(null);
    
    try {
      console.log(`Refreshing dashboard data for ${selectedRange.days} days...`);
      const response = await apiCall(
        `/subscription/dashboard?days=${selectedRange.days}`, 
        session.access_token,
        undefined, // signal
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );
      
      console.log('🔍 Refresh API response:', response);
      
      const filteredData = {
        ...response.data,
        filtered_period: selectedRange.days
      };
      setDashboardData(filteredData);

      // Extract subscription and billing data from refresh response
      if (response.data?.subscription) {
        setSubscriptionData(response.data.subscription);
        // Update addon settings from subscription data
        setAddonSettings({
          manual_auth: response.data.subscription.addon_manual_auth || false,
          manual_maintenance: response.data.subscription.addon_manual_maintenance || false,
          manual_auth_platforms: response.data.subscription.addon_manual_auth_platforms || 0,
          manual_maintenance_platforms: response.data.subscription.addon_manual_maintenance_platforms || 0
        });
        console.log('🔍 Refresh: Subscription data extracted:', response.data.subscription);
      }
      
      if (response.data?.billing) {
        setBillingData(response.data.billing);
        console.log('🔍 Refresh: Billing data extracted:', response.data.billing);
      }

      if (response.budget?.currentMonthlyBudget !== undefined) {
        setBudget(response.budget.currentMonthlyBudget);
      }
    } catch (err: any) {
      console.error('REFRESH: Failed to refresh dashboard data:', err);
      setError(err.message || 'Failed to refresh usage data');
    } finally {
      setRefreshing(false);
    }
  }, [selectedRange.days, session?.access_token]);


  // Handle budget update
  const handleBudgetUpdate = useCallback((newBudget: number) => {
    setBudget(newBudget);
  }, []);

  // Fetch available plans from backend
  const fetchAvailablePlans = useCallback(async () => {
    if (!session?.access_token) return;
    
    try {
      setPlansLoading(true);
      console.log('🔄 Fetching available subscription plans...');
      
      const response = await apiCall('/subscription/plans', session.access_token);
      
      if (response.success && response.plans) {
        setAvailablePlans(response.plans);
        console.log('✅ Plans loaded:', response.plans);
      } else {
        console.error('❌ Invalid plans response:', response);
      }
      
    } catch (error: any) {
      console.error('❌ Failed to fetch plans:', error);
      // Set fallback empty array to prevent UI issues
      setAvailablePlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [session?.access_token]);

  // Handle plan update
  const handlePlanUpdate = useCallback(async (newPlan: any) => {
    if (!newPlan || !session?.access_token || planUpdating) return;
    
    try {
      setPlanUpdating(true);
      console.log('🔄 Updating plan to:', newPlan);
      
      const response = await apiCall('/subscription/update-plan', session.access_token, undefined, {
        method: 'POST',
        body: JSON.stringify({
          plan_id: newPlan.plan_id,
          credits_limit: newPlan.credits_limit,
          preserve_usage: true // Always preserve existing usage
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Plan update response:', response);
      
      if (response.success) {
        // Close modal immediately after success
        setShowPlanModal(false);
        setSelectedPlan(null);
        
        // Then refresh dashboard data to get updated subscription info
        await handleRefresh();
        
      } else {
        throw new Error(response.message || 'Plan update failed');
      }
      
    } catch (error: any) {
      console.error('❌ Plan update failed:', error);
      setError(`Failed to update plan: ${error.message}`);
    } finally {
      setPlanUpdating(false);
    }
  }, [session?.access_token, handleRefresh, planUpdating]);

  // Handle add-on update
  const handleAddonUpdate = useCallback(async () => {
    if (!session?.access_token || addonUpdating) return;
    
    // Validate platform inputs
    if ((addonSettings.manual_auth && addonSettings.manual_auth_platforms < 1) ||
        (addonSettings.manual_maintenance && addonSettings.manual_maintenance_platforms < 1)) {
      alert('Platform count must be at least 1 when an add-on service is enabled.');
      return;
    }
    
    try {
      setAddonUpdating(true);
      console.log('🔄 Updating add-ons:', addonSettings);
      
      const response = await apiCall('/subscription/addons', session.access_token, undefined, {
        method: 'PUT',
        body: JSON.stringify({
          manual_auth: addonSettings.manual_auth,
          manual_maintenance: addonSettings.manual_maintenance,
          manual_auth_platforms: addonSettings.manual_auth_platforms,
          manual_maintenance_platforms: addonSettings.manual_maintenance_platforms
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Add-on update response:', response);
      
      if (response.success) {
        setShowAddonsModal(false);
        
        // Refresh page without cache to ensure fresh data
        window.location.reload();
        
      } else {
        throw new Error(response.message || 'Add-on update failed');
      }
      
    } catch (error: any) {
      console.error('❌ Add-on update failed:', error);
      setError(`Failed to update add-ons: ${error.message}`);
    } finally {
      setAddonUpdating(false);
    }
  }, [session?.access_token, addonSettings, addonUpdating]);


  // Use chart data from API (already filtered by selected date range)
  const mainData = dashboardData?.chart_data || [];
  
  // Calculate totals from API data or fallback
  const summary = dashboardData?.summary || {
    total_calls: 0,
    total_cost: 0,
    total_errors: 0,
    active_endpoints: 0,
    period_days: selectedRange.days
  };
  
  // Recalculate totals based on filtered chart data for consistency
  const filteredTotalCalls = mainData.reduce((sum, day) => sum + (day.calls || 0), 0);
  const filteredTotalCost = mainData.reduce((sum, day) => sum + (day.cost || 0), 0);
  
  // Get endpoints data from API
  const endpointsData = dashboardData?.endpoints || [];
  
  // Use filtered totals for consistency with the selected date range
  const totalCalls = filteredTotalCalls;
  const totalCost = filteredTotalCost;
  const daysUntilReset = 21; // TODO: Calculate from billing cycle
  const activeEndpoints = summary.active_endpoints;
  const totalEndpoints = summary.total_endpoints || endpointsData.length;
  
  // Calculate current month billing period
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const billingPeriod = `${monthStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}-${monthEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  
  // Calculate selected period display
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - selectedRange.days + 1);
  const periodDisplay = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}-${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  
  
  // Get monthly billing data (always current month, regardless of selected date range)
  const monthlyEndpointsUsed = summary.monthly_endpoints_used || 0;
  const monthlyUsageCalls = summary.monthly_usage_calls || 0;
  
  const monthlyUsageCost = monthlyUsageCalls * 0.10;
  const monthlyMinimumCost = monthlyEndpointsUsed * 20;
  const totalMonthlyCost = monthlyUsageCost + monthlyMinimumCost;
  
  const monthlyBilling = {
    monthly_endpoints_used: monthlyEndpointsUsed,
    monthly_usage_calls: monthlyUsageCalls,
    monthly_usage_cost: monthlyUsageCost,
    total_monthly_cost: totalMonthlyCost
  };
  
  // Pricing breakdown - chart shows selected period, billing shows monthly
  const usageCost = totalCalls * 0.10; // Cost for selected period (chart display)
  
  // Filtered endpoints for search and filters
  const filteredEndpoints = endpointsData.filter(endpoint => {
    const searchName = endpoint.display_name || endpoint.name;
    const matchesSearch = searchName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesApiCalls = !apiCallsFilter || endpoint.calls >= parseInt(apiCallsFilter);
    const matchesDollars = !dollarsFilter || endpoint.cost >= parseFloat(dollarsFilter);
    return matchesSearch && matchesApiCalls && matchesDollars;
  });
  
  // Cost breakdown data from API - now showing individual endpoints
  // Generate colors for endpoints dynamically
  const endpointColors = [
    '#60a5fa', // Blue
    '#34d399', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#10b981', // Emerald
    '#f97316', // Orange
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#71717a'  // Gray for "Others"
  ];
  
  // Calculate pie chart data based on view mode (calls vs cost)
  const monthlyBreakdownTotal = viewMode === 'usage' 
    ? dashboardData?.cost_breakdown?.reduce((sum, item) => sum + (item.calls || 0), 0) || 0
    : dashboardData?.cost_breakdown?.reduce((sum, item) => sum + item.value, 0) || 0;
  
  const pieData = dashboardData?.cost_breakdown?.map((item, index) => ({
    name: item.name,
    value: viewMode === 'usage' ? (item.calls || 0) : item.value, // Use calls for usage mode, cost for cost mode
    displayValue: monthlyBreakdownTotal > 0 
      ? Math.round(((viewMode === 'usage' ? (item.calls || 0) : item.value) / monthlyBreakdownTotal) * 100) 
      : 0,
    color: item.name === 'Others' ? '#71717a' : endpointColors[index % (endpointColors.length - 1)],
    calls: item.calls,
    avg_cost: item.avg_cost
  })) || [
    { name: 'No data', value: 0, displayValue: 100, color: '#71717a' }
  ];

  // Handle PDF export (defined after all variables)
  const handleExport = useCallback(async () => {
    if (isExporting || !dashboardData) return;
    
    setIsExporting(true);
    try {
      const exportData = {
        summary: {
          totalCalls: filteredTotalCalls,
          totalCost: filteredTotalCost,
          activeEndpoints: activeEndpoints,
          periodDays: selectedRange.days,
          periodDisplay: periodDisplay,
        },
        monthlyBilling: {
          monthlyUsageCalls: monthlyUsageCalls,
          monthlyUsageCost: monthlyUsageCost,
          monthlyMinimumCost: monthlyMinimumCost,
          totalMonthlyCost: totalMonthlyCost,
          monthlyEndpointsUsed: monthlyEndpointsUsed,
        },
        endpoints: filteredEndpoints.slice(0, 20).map(ep => ({
          name: ep.display_name || ep.name,
          calls: ep.calls,
          cost: ep.cost,
          successRate: ep.success_rate,
        })),
        costBreakdown: pieData,
        chartElement: mainChartRef.current,
        pieChartElement: pieChartRef.current,
        userName: user?.user_metadata?.full_name,
        userEmail: user?.email,
      };

      await generateUsageReportPDF(exportData);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, dashboardData, filteredTotalCalls, filteredTotalCost, activeEndpoints, selectedRange.days, periodDisplay, filteredEndpoints, pieData, user]);

  console.log('🚀 About to render UsageDashboard JSX...');
  
  return (
    <div className="flex flex-col h-screen w-full bg-zinc-950">
      <SkeletonStyles />
      {/* Header */}
      <div className="bg-zinc-900 px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-zinc-200 font-semibold text-lg">Usage</h1>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Date Range Selector */}
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-sm text-zinc-200 transition-all duration-200"
            >
              <span>{selectedRange.label}</span>
              <span className="text-zinc-400 text-xs">▼</span>
            </button>
            
            {showDatePicker && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-10 overflow-hidden">
                {dateRanges.map((range) => (
                  <button
                    key={range.days}
                    className="w-full px-4 py-2.5 text-left text-sm text-zinc-200 hover:bg-zinc-800 transition-colors"
                    onClick={() => handleDateRangeChange(range)}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          
          {/* Refresh Buttons */}
          <div className="flex gap-2">
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className={`px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-sm text-zinc-200 transition-all duration-200 ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {refreshing ? 'Loading...' : 'Refresh'}
            </button>
            {/* <button 
              onClick={handleForceRefresh}
              disabled={refreshing}
              className={`px-3 py-1.5 bg-orange-800/80 hover:bg-orange-700 border border-orange-700/50 rounded-lg text-sm text-orange-200 transition-all duration-200 ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Force refresh and clear all caches"
            >
              {refreshing ? '...' : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button> */}
          </div>
          
          {/* Export Button */}
          <button 
            onClick={handleExport}
            disabled={isExporting || loading}
            className={`px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-sm text-zinc-200 transition-all duration-200 ${(isExporting || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isExporting ? 'Generating PDF...' : 'Export PDF'}
          </button>

          {/* Back to Chat */}
          <button
            onClick={() => window.location.href = '/'}
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
          >
            ← Back to Chat
          </button>
          
          {/* User Info */}
          {user && (
            <div className="flex items-center gap-2">
              {user.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt="User avatar"
                  className="w-6 h-6 rounded-full object-cover border border-zinc-600"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                  {(user.user_metadata?.full_name || user.email || '').charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-zinc-300 text-sm">{user.user_metadata?.full_name || 'User'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {(loading || refreshing) && <DashboardSkeleton />}
      
      {/* Error State */}
      {error && !loading && !refreshing && (
        <div className="flex-1 flex items-center justify-center bg-zinc-950">
          <div className="text-center max-w-md">
            <div className="text-red-400 text-xl mb-2">⚠️ Error Loading Data</div>
            <div className="text-zinc-400 mb-4">{error}</div>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
            <div className="text-xs text-zinc-500 mt-2">
              Using fallback data for development
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!loading && !refreshing && (
      <div className="flex-1 px-4 py-4 overflow-y-auto">
        <div className="w-full pb-6">
          {/* Main Graph and Budget Section */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            {/* Main Usage/Cost Graph */}
            <div className="lg:col-span-3 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              {/* View Mode Toggle - Calls prioritized */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-zinc-200">
                  API {viewMode === 'usage' ? 'Calls' : 'Cost'} Between {periodDisplay}
                </h2>
                <div className="flex bg-zinc-800 rounded-lg p-1">
                  <button
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                      viewMode === 'usage'
                        ? 'bg-zinc-700 text-zinc-200'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    onClick={() => setViewMode('usage')}
                  >
                    Calls
                  </button>
                  {/* <button
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                      viewMode === 'cost'
                        ? 'bg-zinc-700 text-zinc-200'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    onClick={() => setViewMode('cost')}
                  >
                    Cost (Dev)
                  </button> */}
                </div>
              </div>

              {/* Graph */}
              <div className="h-80" ref={mainChartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mainData} margin={{ left: 0, right: 0, top: 20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#71717a', fontSize: 11 }}
                      tickMargin={8}
                      axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={viewMode === 'usage' ? fmtNum : (val: number) => fmtUSD(val * 0.10)}
                      tick={{ fill: '#71717a', fontSize: 11 }}
                      width={60}
                      axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'rgba(24, 24, 27, 0.95)',
                        border: '1px solid rgba(63, 63, 70, 0.5)',
                        borderRadius: 8,
                        backdropFilter: 'blur(4px)',
                      }}
                      labelStyle={{ color: '#e4e4e7', fontSize: 12 }}
                      formatter={(val: number) =>
                        viewMode === 'usage' ? fmtNum(val) : fmtUSD(val * 0.10)
                      }
                      cursor={{ stroke: 'rgba(96, 165, 250, 0.3)', strokeWidth: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey={viewMode === 'usage' ? 'calls' : 'calls'}
                      stroke="#60a5fa"
                      strokeWidth={2}
                      fill="url(#lineGradient)"
                      dot={{ fill: '#60a5fa', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#60a5fa' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Total Stats - Calls prioritized */}
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-zinc-400 mb-1">Total API Calls</div>
                    <div className="text-2xl font-semibold text-zinc-200">{fmtNum(totalCalls)}</div>
                    <div className="text-xs text-zinc-500 mt-1">calls made</div>
                  </div>
                  <div>
                    <div className="text-sm text-zinc-400 mb-1">Active Endpoints</div>
                    <div className="text-2xl font-semibold text-zinc-200">{activeEndpoints}</div>
                    <div className="text-xs text-zinc-500 mt-1">endpoints used</div>
                  </div>
                </div>
                {/* Cost info moved to developer section - can be hidden later */}
                {viewMode === 'cost' && (
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <div className="text-sm text-zinc-500 mb-2">Developer: Legacy Cost Calculation</div>
                    <div className="text-lg font-medium text-zinc-300">{fmtUSD(usageCost)}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Usage Panel - Credits Focused */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-zinc-200 mb-6">Usage for {billingPeriod}</h3>
              
              {/* Call Usage Display with Progress Bar */}
              <div className="mb-4">
                <div className="text-2xl font-semibold text-zinc-200 mb-2">
                  {subscriptionData && subscriptionData.credits_limit > 0 ? (
                    `${fmtNum(subscriptionData.credits_used || 0)}/${fmtNum(subscriptionData.credits_limit)} credits`
                  ) : (
                    `${fmtNum(monthlyBilling.monthly_usage_calls)}/1,000 calls`
                  )}
                </div>
                <div className="text-sm text-zinc-400 mb-4">
                  {subscriptionData && subscriptionData.credits_limit > 0 ? (
                    `Monthly plan: ${fmtNum(subscriptionData.credits_limit)} credits`
                  ) : (
                    'Monthly plan: 1,000 credits (loading...)'
                  )}
                </div>
                
                {/* Progress Bar for Call Usage */}
                <div className="w-full bg-zinc-800 rounded-full h-2 mb-4">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: subscriptionData 
                        ? `${Math.min(subscriptionData.usage_percentage || 0, 100)}%`
                        : `${Math.min((monthlyBilling.monthly_usage_calls / 1000) * 100, 100)}%`
                    }}
                  />
                </div>
                
                {/* Usage Status */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-zinc-400">
                    {subscriptionData && subscriptionData.credits_limit > 0 ? (
                      subscriptionData.credits_remaining > 0
                        ? `${fmtNum(subscriptionData.credits_remaining)} credits remaining`
                        : 'Plan limit exceeded'
                    ) : (
                      `${fmtNum(1000 - monthlyBilling.monthly_usage_calls)} calls remaining`
                    )}
                  </span>
                  <span className="text-sm text-zinc-400">
                    {subscriptionData ? (
                      `${subscriptionData.usage_percentage?.toFixed(1) || '0.0'}% used`
                    ) : (
                      `${((monthlyBilling.monthly_usage_calls / 1000) * 100).toFixed(1)}% used`
                    )}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-zinc-400">
                    Resets in {subscriptionData?.days_until_reset || daysUntilReset} days
                  </div>
                  
                  {/* Update Plan Button - Prominent as shown in wireframe */}
                  <button 
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
                    onClick={() => {
                      setShowPlanModal(true);
                      fetchAvailablePlans(); // Fetch plans when modal opens
                    }}
                  >
                    Update Plan
                  </button>
                </div>
              </div>

              {/* Pricing Breakdown - As shown in wireframe */}
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="text-sm font-medium text-zinc-300 mb-4">Pricing Breakdown</div>
                
                {/* Monthly Plan */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Monthly Plan</span>
                    <span className="font-semibold text-zinc-200">
                      {billingData ? fmtUSD(billingData.plan_cost) : '$100'}
                    </span>
                  </div>
                  
                  {/* Monthly Minimums from Add-ons */}
                  {billingData?.addon_costs?.map((addon: any, index: number) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-zinc-400">+ {addon.description}</span>
                      <span className="font-semibold text-zinc-200">{fmtUSD(addon.cost)}</span>
                    </div>
                  ))}

                  {/* Browserbase Credential Credit Usage */}
                  {billingData?.browserbase_credential_credits > 0 && (
                    <div className="flex items-center justify-between text-blue-400">
                      <span>→ Stored Credentials ({billingData.browserbase_credential_count} accounts)</span>
                      <span className="font-semibold">{billingData.browserbase_credential_credits} credits/month</span>
                    </div>
                  )}

                  {/* Fallback for when no billing data */}
                  {!billingData && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400">+ Manual Auth (2 platforms)</span>
                        <span className="font-semibold text-zinc-200">$200</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400">+ Maintenance (1 platform)</span>
                        <span className="font-semibold text-zinc-200">$75</span>
                      </div>
                    </>
                  )}
                  
                  {/* Show total monthly minimums and whether plan surpasses them */}
                  {billingData ? (
                    <>
                      {billingData.total_addon_minimum > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-400">= {fmtUSD(billingData.total_addon_minimum)} monthly minimum</span>
                          <span className={`font-semibold ${billingData.minimum_surpassed ? 'text-green-400' : 'text-zinc-200'}`}>
                            {billingData.minimum_surpassed ? 'Surpassed!' : fmtUSD(billingData.total_addon_minimum)}
                          </span>
                        </div>
                      )}
                      
                      {/* Total */}
                      <div className="border-t border-zinc-700 pt-2 mt-3 flex items-center justify-between">
                        <span className="font-medium text-zinc-200">Total</span>
                        <span className="text-lg font-semibold text-zinc-200">{fmtUSD(billingData.final_monthly_cost)}</span>
                      </div>
                    </>
                  ) : (
                    /* Fallback calculation */
                    (() => {
                      const planCost = 100;
                      const totalMinimums = 275;
                      const finalCost = Math.max(planCost, totalMinimums);
                      const surpassed = planCost >= totalMinimums;
                      
                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-zinc-400">= ${totalMinimums} monthly minimum</span>
                            <span className={`font-semibold ${surpassed ? 'text-green-400' : 'text-zinc-200'}`}>
                              {surpassed ? 'Surpassed!' : `$${totalMinimums}`}
                            </span>
                          </div>
                          
                          {/* Total */}
                          <div className="border-t border-zinc-700 pt-2 mt-3 flex items-center justify-between">
                            <span className="font-medium text-zinc-200">Total</span>
                            <span className="text-lg font-semibold text-zinc-200">${finalCost}</span>
                          </div>
                        </>
                      );
                    })()
                  )}
                </div>
                
                {/* Add-on management */}
                <div className="mt-4 pt-4 border-t border-zinc-700">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-zinc-500">
                      Add-on services (Manual Auth, Maintenance)
                    </div>
                    <button 
                      className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-xs rounded transition-colors"
                      onClick={() => setShowAddonsModal(true)}
                    >
                      Manage Add-ons
                    </button>
                  </div>
                  
                  {/* Current add-on status */}
                  {(subscriptionData?.addon_manual_auth || subscriptionData?.addon_manual_maintenance) && (
                    <div className="mt-2 text-xs text-zinc-400">
                      Active: {[
                        subscriptionData?.addon_manual_auth && `Manual Auth (${subscriptionData?.addon_manual_auth_platforms || 0} platforms)`,
                        subscriptionData?.addon_manual_maintenance && `Manual Maintenance (${subscriptionData?.addon_manual_maintenance_platforms || 0} platforms)`
                      ].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* Developer: Legacy Cost Info */}
              {viewMode === 'cost' && (
                <div className="mt-6 pt-6 border-t border-zinc-800">
                  <div className="text-xs font-medium text-zinc-500 mb-2">Developer: Legacy Cost Breakdown</div>
                  <div className="space-y-1.5 text-xs text-zinc-500">
                    <div className="flex items-center justify-between">
                      <span>Monthly usage × $0.10</span>
                      <span>{fmtUSD(monthlyUsageCost)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Endpoints × $20</span>
                      <span>{fmtUSD(monthlyMinimumCost)}</span>
                    </div>
                    <div className="border-t border-zinc-700 pt-1.5 mt-2 flex items-center justify-between text-zinc-400">
                      <span>Total</span>
                      <span>{fmtUSD(totalMonthlyCost)}</span>
                    </div>
                  </div>
                  
                  <button 
                    className="text-sm text-blue-400 hover:text-blue-300 mt-4"
                    onClick={() => setShowBudgetModal(true)}
                  >
                    Edit legacy budget
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sub-section Toggle - Calls Focused */}
          <div className="mb-6">
            <div className="flex items-center gap-4 pb-4 border-b border-zinc-800">
              <button
                className={`text-sm font-medium transition-colors ${
                  subView === 'endpoints'
                    ? 'text-zinc-200 border-b-2 border-blue-500 pb-4 -mb-[17px]'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                onClick={() => setSubView('endpoints')}
              >
                Endpoint Usage
              </button>
              <button
                className={`text-sm font-medium transition-colors ${
                  subView === 'breakdown'
                    ? 'text-zinc-200 border-b-2 border-blue-500 pb-4 -mb-[17px]'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                onClick={() => setSubView('breakdown')}
              >
                Usage Breakdown
              </button>
            </div>
          </div>

          {/* Sub-section Content */}
          {subView === 'endpoints' ? (
            <div>
              {/* Search and Filter Bar */}
              <div className="mb-6 flex items-center gap-3">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-4 py-2.5 bg-zinc-900/60 border border-zinc-700/50 rounded-xl text-sm text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                    placeholder="Search endpoints..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-zinc-400 hover:text-zinc-200"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Filter Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-3 py-2.5 bg-zinc-900/60 border border-zinc-700/50 rounded-xl text-sm transition-all duration-200 hover:bg-zinc-800/60 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${showFilters ? 'bg-zinc-800/60 border-blue-500/50' : ''}`}
                  >
                    <svg className="h-4 w-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </button>
                  
                  {/* Filter Popup */}
                  {showFilters && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowFilters(false)}
                      />
                      <div className="absolute top-full right-0 mt-2 w-72 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl z-20 overflow-hidden">
                        <div className="p-4">
                        <h4 className="text-sm font-medium text-zinc-200 mb-3">Filter by</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-zinc-400 mb-1">API Calls ≥</label>
                            <input
                              type="number"
                              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50"
                              placeholder="e.g. 100"
                              value={apiCallsFilter}
                              onChange={(e) => setApiCallsFilter(e.target.value)}
                            />
                          </div>
                          {viewMode === 'cost' && (
                            <div>
                              <label className="block text-xs text-zinc-500 mb-1">Legacy: Dollars Spent ≥</label>
                              <input
                                type="number"
                                step="0.01"
                                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-400 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50"
                                placeholder="e.g. 50.00"
                                value={dollarsFilter}
                                onChange={(e) => setDollarsFilter(e.target.value)}
                              />
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-2">
                            <button
                              onClick={() => {
                                setApiCallsFilter('');
                                setDollarsFilter('');
                              }}
                              className="text-xs text-zinc-400 hover:text-zinc-200"
                            >
                              Clear filters
                            </button>
                            <button
                              onClick={() => setShowFilters(false)}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg transition-colors"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Results Counter */}
              {(searchQuery || apiCallsFilter || dollarsFilter) && (
                <div className="mb-4 text-center">
                  <span className="text-sm text-zinc-400">
                    {filteredEndpoints.length} of {endpointsData.length} endpoints
                    {(apiCallsFilter || dollarsFilter) && (
                      <span className="ml-2 text-xs text-blue-400">
                        (filtered)
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Endpoints Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredEndpoints.length > 0 ? filteredEndpoints.map((endpoint, index) => (
                <div
                  key={endpoint.name}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 cursor-pointer hover:border-zinc-700 transition-all duration-200"
                  onClick={() => handleEndpointClick(endpoint.name)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-zinc-200">{(() => {
                          let displayName = endpoint.display_name || endpoint.name;
                          if (displayName.includes(':')) {
                            displayName = displayName.split(':')[1];
                          }
                          return displayName;
                        })()}</h4>
                        <span className={`w-2 h-2 rounded-full ${
                          endpoint.route_status === 'DEACTIVATED' ? 'bg-red-400' : 
                          endpoint.status === 'active' || endpoint.calls > 0 ? 'bg-green-400' : 'bg-zinc-500'
                        }`} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-400">
                        <span>{endpoint.latency}ms latency</span>
                        {endpoint.success_rate && (
                          <span className={endpoint.success_rate > 95 ? 'text-green-400' : endpoint.success_rate > 90 ? 'text-yellow-400' : 'text-red-400'}>
                            {endpoint.success_rate.toFixed(1)}% success
                          </span>
                        )}
                        <span className={
                          endpoint.route_status === 'DEACTIVATED' ? 'text-red-400' : 
                          endpoint.status === 'active' ? 'text-green-400' : 'text-zinc-500'
                        }>
                          {endpoint.route_status === 'DEACTIVATED' ? 'deactivated' : endpoint.status}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-zinc-200">
                        {viewMode === 'usage' ? fmtNum(endpoint.calls) : fmtUSD(endpoint.calls * 0.10)}
                      </div>
                      <div className="text-xs text-zinc-400">
                        {viewMode === 'usage' ? 'calls' : 'cost'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Mini Graph - Simple line chart */}
                  <div className="h-16 relative">
                    <svg className="w-full h-full" viewBox="0 0 140 64">
                      <defs>
                        <linearGradient id={`gradient-${endpoint.name}`} x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {/* Generate simple line path - if no usage, show flat line */}
                      {(() => {
                        const points = Array.from({ length: 7 }, (_, i) => {
                          const x = (i / 6) * 140;
                          // If endpoint has usage, show some variation, otherwise flat line
                          const y = endpoint.calls > 0 ? 64 - (Math.random() * 40 + 20) : 50;
                          return `${x},${y}`;
                        });
                        const pathData = `M ${points.join(' L ')}`;
                        const fillPath = `${pathData} L 140,64 L 0,64 Z`;
                        
                        return (
                          <>
                            <path
                              d={fillPath}
                              fill={`url(#gradient-${endpoint.name})`}
                            />
                            <path
                              d={pathData}
                              stroke="#60a5fa"
                              strokeWidth="1.5"
                              fill="none"
                            />
                          </>
                        );
                      })()}
                    </svg>
                  </div>
                </div>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <div className="text-zinc-400 text-lg mb-2">No endpoints found</div>
                    <div className="text-zinc-500 text-sm">Try adjusting your search query</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-zinc-200 mb-2 text-center">
                {viewMode === 'usage' ? 'Monthly Usage Breakdown' : 'Monthly Cost Breakdown'}
              </h3>
              <p className="text-sm text-zinc-400 mb-6 text-center">
                {viewMode === 'usage' ? 'Current month API calls by endpoint' : 'Legacy cost breakdown by endpoint'}
              </p>
              <div className="flex items-center justify-center" ref={pieChartRef}>
                <div className="w-64 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="displayValue"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: 'rgba(24, 24, 27, 0.95)',
                          border: '1px solid rgba(63, 63, 70, 0.5)',
                          borderRadius: 8,
                          backdropFilter: 'blur(4px)',
                          color: '#e4e4e7',
                        }}
                        labelStyle={{ color: '#e4e4e7', fontSize: 12 }}
                        formatter={(value: number, name: string, props: any) => {
                          const item = props.payload;
                          if (viewMode === 'usage') {
                            return `${value}% (${fmtNum(item.value)} calls)`;
                          } else {
                            return [
                              `${value}% (${fmtUSD(item.value)})`,
                              item.calls ? `${fmtNum(item.calls)} calls` : ''
                            ].filter(Boolean).join(' • ');
                          }
                        }}
                        itemStyle={{ color: '#e4e4e7' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="ml-8 max-w-md">
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {pieData.map((item) => (
                      <div key={item.name} className="flex items-center justify-between gap-3 p-2 hover:bg-zinc-800/50 rounded">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div
                            className="w-3 h-3 rounded flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-sm text-zinc-200 truncate">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-zinc-400">
                            {viewMode === 'usage' ? `${fmtNum(item.value)} calls` : fmtUSD(item.value)}
                          </span>
                          <span className="text-zinc-500">{item.displayValue}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-800 text-xs text-zinc-500">
                    {viewMode === 'usage' ? 'Showing top endpoints by calls' : 'Showing top endpoints by cost'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Endpoint Detail Modal */}
      {selectedEndpoint && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5"
          onClick={() => {
            setSelectedEndpoint(null);
            setSelectedEndpointData(null);
          }}
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <h3 className="text-lg font-semibold text-zinc-200">
                {(() => {
                  let displayName = endpointsData.find(e => e.name === selectedEndpoint)?.display_name || selectedEndpoint;
                  if (displayName.includes(':')) {
                    displayName = displayName.split(':')[1];
                  }
                  return displayName;
                })()}
              </h3>
              <button
                className="text-zinc-400 hover:text-zinc-200 text-2xl transition-colors duration-200"
                onClick={() => {
                  setSelectedEndpoint(null);
                  setSelectedEndpointData(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="p-6">
              {endpointDataLoading ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <div className="text-zinc-400">Loading endpoint data...</div>
                  </div>
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={selectedEndpointData?.daily_data || []} 
                      margin={{ left: 0, right: 0, top: 20, bottom: 0 }}
                    >
                    <defs>
                      <linearGradient id="modalLineGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#71717a', fontSize: 11 }}
                      tickMargin={8}
                      axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={viewMode === 'usage' ? fmtNum : (val: number) => fmtUSD(val * 0.10)}
                      tick={{ fill: '#71717a', fontSize: 11 }}
                      width={60}
                      axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'rgba(24, 24, 27, 0.95)',
                        border: '1px solid rgba(63, 63, 70, 0.5)',
                        borderRadius: 8,
                        backdropFilter: 'blur(4px)',
                      }}
                      labelStyle={{ color: '#e4e4e7', fontSize: 12 }}
                      formatter={(val: number) =>
                        viewMode === 'usage' ? fmtNum(val) : fmtUSD(val * 0.10)
                      }
                      cursor={{ stroke: 'rgba(96, 165, 250, 0.3)', strokeWidth: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey={viewMode === 'usage' ? 'calls' : 'calls'}
                      stroke="#60a5fa"
                      strokeWidth={2}
                      fill="url(#modalLineGradient)"
                      dot={{ fill: '#60a5fa', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#60a5fa' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                
                {/* Endpoint Stats */}
                {selectedEndpointData?.data && (
                  <div className="mt-6 pt-6 border-t border-zinc-800">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="text-sm text-zinc-400 mb-1">Total Calls</div>
                        <div className="text-2xl font-semibold text-zinc-200">{fmtNum(selectedEndpointData.data.calls || 0)}</div>
                        <div className="text-xs text-zinc-500 mt-1">for this endpoint</div>
                      </div>
                      <div>
                        <div className="text-sm text-zinc-400 mb-1">Success Rate</div>
                        <div className="text-2xl font-semibold text-zinc-200">{selectedEndpointData.data.success_rate?.toFixed(1) || '100.0'}%</div>
                        <div className="text-xs text-zinc-500 mt-1">successful calls</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Budget Edit Modal */}
      <BudgetEditModal
        isOpen={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        currentBudget={budget}
        onBudgetUpdate={handleBudgetUpdate}
      />

      {/* Plan Selection Modal with Pricing Slider */}
      <PricingSlider
        isOpen={showPlanModal}
        onClose={() => {
          setShowPlanModal(false);
          setSelectedPlan(null);
        }}
        onPlanSelect={handlePlanUpdate}
        currentPlan={subscriptionData ? {
          plan_id: subscriptionData.plan_id || '',
          name: '', // Will be populated by the component
          description: '',
          credits_limit: subscriptionData.credits_limit || 0,
          monthly_cost: 0 // Will be populated by the component
        } : undefined}
        loading={planUpdating}
      />

      {/* Add-on Management Modal */}
      {showAddonsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-zinc-200">Manage Add-on Services</h3>
              <button
                onClick={() => setShowAddonsModal(false)}
                className="text-zinc-400 hover:text-zinc-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Manual Auth Service */}
              <div className="p-4 border border-zinc-700 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-zinc-200">Manual Authentication</h4>
                    <p className="text-sm text-zinc-400">Handle complex authentication flows manually</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={addonSettings.manual_auth}
                      onChange={(e) => setAddonSettings(prev => ({
                        ...prev,
                        manual_auth: e.target.checked,
                        manual_auth_platforms: e.target.checked ? Math.max(prev.manual_auth_platforms, 1) : 0
                      }))}
                    />
                    <div className="w-11 h-6 bg-zinc-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {/* Platform count for Manual Auth */}
                {addonSettings.manual_auth && (
                  <div className="mt-3 mb-3">
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Number of Platforms for Manual Auth
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={addonSettings.manual_auth_platforms}
                      onChange={(e) => setAddonSettings(prev => ({
                        ...prev,
                        manual_auth_platforms: parseInt(e.target.value) || 1
                      }))}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-zinc-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                
                <div className="text-xs text-zinc-500">
                  Base cost: $100/month per platform
                  {addonSettings.manual_auth && addonSettings.manual_auth_platforms > 0 && (
                    <span className="ml-2 text-blue-400 font-medium">
                      = {fmtUSD(100 * addonSettings.manual_auth_platforms)}/month
                    </span>
                  )}
                </div>
              </div>

              {/* Manual Maintenance Service */}
              <div className="p-4 border border-zinc-700 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-zinc-200">Manual Maintenance</h4>
                    <p className="text-sm text-zinc-400">Regular maintenance and monitoring services</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={addonSettings.manual_maintenance}
                      onChange={(e) => setAddonSettings(prev => ({
                        ...prev,
                        manual_maintenance: e.target.checked,
                        manual_maintenance_platforms: e.target.checked ? Math.max(prev.manual_maintenance_platforms, 1) : 0
                      }))}
                    />
                    <div className="w-11 h-6 bg-zinc-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {/* Platform count for Manual Maintenance */}
                {addonSettings.manual_maintenance && (
                  <div className="mt-3 mb-3">
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Number of Platforms for Manual Maintenance
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={addonSettings.manual_maintenance_platforms}
                      onChange={(e) => setAddonSettings(prev => ({
                        ...prev,
                        manual_maintenance_platforms: parseInt(e.target.value) || 1
                      }))}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-zinc-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                
                <div className="text-xs text-zinc-500">
                  Base cost: $75/month per platform
                  {addonSettings.manual_maintenance && addonSettings.manual_maintenance_platforms > 0 && (
                    <span className="ml-2 text-blue-400 font-medium">
                      = {fmtUSD(75 * addonSettings.manual_maintenance_platforms)}/month
                    </span>
                  )}
                </div>
              </div>

              {/* Cost Summary */}
              {(addonSettings.manual_auth || addonSettings.manual_maintenance) && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="text-sm font-medium text-blue-400 mb-2">Monthly Cost Estimate:</div>
                  <div className="space-y-1 text-xs text-zinc-300">
                    {addonSettings.manual_auth && addonSettings.manual_auth_platforms > 0 && (
                      <div>Manual Auth: {fmtUSD(100 * addonSettings.manual_auth_platforms)} ({addonSettings.manual_auth_platforms} platforms)</div>
                    )}
                    {addonSettings.manual_maintenance && addonSettings.manual_maintenance_platforms > 0 && (
                      <div>Manual Maintenance: {fmtUSD(75 * addonSettings.manual_maintenance_platforms)} ({addonSettings.manual_maintenance_platforms} platforms)</div>
                    )}
                    <div className="border-t border-blue-500/20 pt-1 mt-2 font-medium text-blue-300">
                      Total Add-ons: {fmtUSD(
                        (addonSettings.manual_auth ? 100 * addonSettings.manual_auth_platforms : 0) +
                        (addonSettings.manual_maintenance ? 75 * addonSettings.manual_maintenance_platforms : 0)
                      )}/month
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddonsModal(false)}
                  disabled={addonUpdating}
                  className={`flex-1 px-4 py-2 border border-zinc-700 rounded-lg transition-colors ${
                    addonUpdating 
                      ? 'text-zinc-500 cursor-not-allowed bg-zinc-800' 
                      : 'text-zinc-200 hover:bg-zinc-800'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddonUpdate}
                  disabled={addonUpdating}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                    addonUpdating
                      ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-500 text-white'
                  }`}
                >
                  {addonUpdating ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating Add-ons...
                    </div>
                  ) : (
                    'Update Add-ons'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default UsageDashboard;
